import { useCallback, useEffect, useMemo, useReducer } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Heading from 'LuxKit/Typography/Heading'
import { sortBy, uniqueBy } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightDealAirportSelect from '../FlightDealAirportSelect'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import Divider from 'LuxKit/Divider'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import type { FlightsClassTypes } from 'constants/flight'
import FilterChip from 'LuxKit/Chips/FilterChip'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextButton from 'LuxKit/Button/TextButton'
import { FlightDealsGridStaticFilterPills } from './FlightsDealsGridStaticFilterPills'
import FlightsDealsGridCard from './FlightsDealsGridCard'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ALL_KEY, FilterType } from '../flightDealsFilter.constants'
import { getFlightDealDestinationFilters } from '../getFlightDealDestinationFilters'

const DividerContainer = styled.div`
  height: ${rem(16)};
  padding: 0 ${rem(4)};
`

const DividerBar = styled(Divider)`
  height: ${rem(16)};
  max-height: ${rem(16)};
`

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${rem(32)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(3, 1fr);
  }
`

type FilterState = Record<FilterType, Set<string>>

enum FilterActionType {
  TOGGLE_FILTER = 'TOGGLE_FILTER',
  SET_FILTER_VALUE = 'SET_FILTER_VALUE',
  RESET_FILTERS = 'RESET_FILTERS',
}

type FilterAction = {
  type: FilterActionType.TOGGLE_FILTER
  payload: {
    type: FilterType
    value: string
  }
} | {
  type: FilterActionType.SET_FILTER_VALUE
  payload: {
    type: FilterType
    value: Set<string>
  }
} | {
  type: FilterActionType.RESET_FILTERS
}

const initialState: FilterState = {
  destinations: new Set([ALL_KEY]),
  airlines: new Set([ALL_KEY]),
  cabins: new Set([ALL_KEY]),
}

function reducer(state: FilterState, action: FilterAction): FilterState {
  if (action.type === FilterActionType.TOGGLE_FILTER) {
    let newData = new Set([...state[action.payload.type]])

    if (action.payload.value === ALL_KEY) {
      const deleted = newData.delete(action.payload.value)
      newData = deleted ? new Set() : new Set([ALL_KEY])
    } else {
      newData.delete(ALL_KEY)
      const deleted = newData.delete(action.payload.value)
      if (!deleted) newData.add(action.payload.value)
    }

    if (newData.size === 0) {
      newData.add(ALL_KEY)
    }

    return {
      ...state,
      [action.payload.type]: newData,
    }
  }

  if (action.type === FilterActionType.SET_FILTER_VALUE) {
    return {
      ...state,
      [action.payload.type]: action.payload.value,
    }
  }

  if (action.type === FilterActionType.RESET_FILTERS) {
    return {
      ...initialState,
    }
  }

  return state
}

interface Props {
  title: string
  showViewAll?: boolean
  airports: Array<App.Airport>
  selectedAirport?: App.Airport
  defaultAirport?: App.Airport
  flightDeals: Array<App.FlightDeal>
  onAirportChange: (airport: App.Airport) => void
  showLookingForAFlight?: boolean
  hideAirlinesFilter?: boolean
}

function FlightDealsGrid(props: Props) {
  const { title, showViewAll, airports, selectedAirport, flightDeals, defaultAirport, onAirportChange, hideAirlinesFilter } = props
  const intl = useIntl()
  const [filtersState, filtersDispatch] = useReducer(reducer, initialState)
  const airport = selectedAirport ?? defaultAirport

  const currentAirportDeals = useMemo(() => {
    if (!airport) return []

    const deals = flightDeals.filter(({ originAirportCode }) => originAirportCode === airport.code)
    return sortBy(deals, (deal) => deal.hierarchy, 'asc')
  }, [airport, flightDeals])

  useEffect(() => {
    filtersDispatch({ type: FilterActionType.RESET_FILTERS })
  }, [airport])

  const destinations = useMemo(() => getFlightDealDestinationFilters(
    currentAirportDeals,
    filtersState.airlines,
    filtersState.cabins,
    intl.locale,
    ALL_KEY,
  ), [currentAirportDeals, filtersState.airlines, filtersState.cabins, intl.locale])

  const airlines = useMemo(() => {
    const carriers = currentAirportDeals.map(deal => ({
      label: deal.carrier.name,
      value: deal.carrier.code,
      price: deal.perAdultPrice,
    }))

    const sortedCarriers = sortBy(carriers, (carrier) => carrier.price, 'asc')
    return uniqueBy(sortedCarriers, carrier => carrier.value)
  }, [currentAirportDeals])

  const cabins = useMemo(() => {
    const data = currentAirportDeals
      .filter(deal => {
        const airlineIncluded = filtersState.airlines.has(deal.carrier.code) || filtersState.airlines.has(ALL_KEY)
        const destinationIncluded = filtersState.destinations.has(deal.destinationCountry) || filtersState.destinations.has(ALL_KEY)
        return airlineIncluded && destinationIncluded
      })
      .map(deal => ({
        label: deal.fareClass,
        value: deal.fareClass as FlightsClassTypes,
        price: deal.perAdultPrice,
      }))

    const sortedCabins = sortBy(data, (data) => data.price, 'asc')
    return uniqueBy(sortedCabins, cabin => cabin.value)
  }, [currentAirportDeals, filtersState.airlines, filtersState.destinations])

  const handleToggleFilterValue = useCallback((type: FilterType, value: string) => {
    filtersDispatch({
      type: FilterActionType.TOGGLE_FILTER,
      payload: {
        type,
        value,
      },
    })

    if (type === FilterType.AIRLINES) {
      filtersDispatch({
        type: FilterActionType.SET_FILTER_VALUE,
        payload: {
          type: FilterType.CABINS,
          value: new Set([ALL_KEY]),
        },
      })

      filtersDispatch({
        type: FilterActionType.SET_FILTER_VALUE,
        payload: {
          type: FilterType.DESTINATIONS,
          value: new Set([ALL_KEY]),
        },
      })
    }

    if (type === FilterType.CABINS) {
      filtersDispatch({
        type: FilterActionType.SET_FILTER_VALUE,
        payload: {
          type: FilterType.DESTINATIONS,
          value: new Set([ALL_KEY]),
        },
      })
    }
  }, [])

  const filteredDeals = useMemo(() => {
    const now = new Date()

    return currentAirportDeals.filter(deal => {
      let show = true

      // Exclude expired deals
      if (deal.salesEndDate) {
        const salesEndDate = new Date(deal.salesEndDate)
        show = show && salesEndDate >= now
      }

      if (filtersState.destinations.size && !filtersState.destinations.has(ALL_KEY)) {
        show = show && filtersState.destinations.has(deal.destinationCountry)
      }

      if (filtersState.airlines.size && !filtersState.airlines.has(ALL_KEY)) {
        show = show && filtersState.airlines.has(deal.carrier.code)
      }

      if (filtersState.cabins.size && !filtersState.cabins.has(ALL_KEY)) {
        show = show && filtersState.cabins.has(deal.fareClass)
      }

      return show
    })
  }, [currentAirportDeals, filtersState])

  const cheapestDestination = useMemo(() => {
    return sortBy(destinations, val => val.price, 'asc')[0]?.price
  }, [destinations])

  return (
    <VerticalSpacer gap={32}>
      <VerticalSpacer gap={32}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={16}>
          <Group direction="horizontal" tabletDirection="horizontal" tabletGap={8} gap={8}>
            <Heading variant="heading2">{title}</Heading>
            {airport && airports.length > 0 &&
              <FlightDealAirportSelect
                selected={airport}
                airports={airports}
                onChange={onAirportChange}
              />}
          </Group>

          {showViewAll && <TextButton kind="tertiary" size="medium" to="/flights">
            {intl.formatMessage({
              id: 'edX3ed62knlw5uRLIZ-Q',
              defaultMessage: 'View all',
              description: 'Button text to view all flight deals in the flight search or booking flow',
            })}
          </TextButton>}
        </Group>

        {flightDeals.length > 0 && (
          <CardCarousel gap={4}>
            <FlightDealsGridStaticFilterPills
              airlines={!hideAirlinesFilter ? airlines : undefined}
              cabins={cabins}
              selectedAirlines={!hideAirlinesFilter ? filtersState.airlines : undefined}
              selectedCabins={filtersState.cabins}
              handleToggle={handleToggleFilterValue}
            />

            <Group direction="vertical" verticalAlign="center">
              <DividerContainer>
                <DividerBar kind="primary" direction="vertical" />
              </DividerContainer>
            </Group>

            {destinations.length > 0 && <FilterChip
              size="medium"
              onClick={() => handleToggleFilterValue(FilterType.DESTINATIONS, ALL_KEY)}
              selected={filtersState.destinations.has(ALL_KEY)}
            >
              <FormattedMessage
                id="xMEu1Gk_62LtK_5MwAHj"
                defaultMessage="<bold>All destinations</bold> {price}*"
                description="Filter chip label showing all destinations with the cheapest price"
                values={{
                  bold: (chunks) => <b>{chunks}</b>,
                  price: <FormatCurrency value={cheapestDestination} />,
                }}
              />
            </FilterChip>}

            {destinations.map(destination => (
              <FilterChip
                key={destination.value}
                size="medium"
                onClick={() => handleToggleFilterValue(FilterType.DESTINATIONS, destination.value)}
                selected={filtersState.destinations.has(destination.value)}
              >
                <FormattedMessage
                  id="98D8KkZwcvmrJ-MKJt6k"
                  defaultMessage="<b>{label}</b> {price}*"
                  description="Flight deal destination filter chip with name, price, and asterisk"
                  values={{
                    label: destination.label,
                    price: <FormatCurrency value={destination.price} />,
                    b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                  }}
                />
              </FilterChip>
            ))}
          </CardCarousel>
        )}
      </VerticalSpacer>

      <GridContainer>
        {filteredDeals.map((flightDeal) => (
          <FlightsDealsGridCard key={flightDeal.id} flightDeal={flightDeal} hasMultipleAirlines />
        ))}
      </GridContainer>
    </VerticalSpacer>
  )
}

export default FlightDealsGrid
